import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, Card, Row, Col, Badge } from "react-bootstrap";
import {
  AlertTriangle,
  Flag,
  Edit2,
  ArrowUp,
  ArrowDown,
  Plus,
  Bell,
  Clock,
  Users,
  MessageSquare,
  Mail,
  Video,
  Calendar,
  CheckCircle,
  XCircle,
} from "react-feather";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { toast } from "react-toastify";
import PageHeader from "../../../Components/shared/PageHeader";
import LoadingSkeleton from "../../../Components/shared/LoadingSkeleton";
import DrawerPanel from "../../../Components/shared/DrawerPanel";
import { companyUrl } from "../../../utils/mhNav";
import styles from "./ReminderMatrix.module.scss";

const CHANNEL_OPTS = ["Teams", "WhatsApp", "Email"];
const TIMING = [
  { m: 5, label: "5 min before", icon: "⚡" },
  { m: 15, label: "15 min before", icon: "⏰" },
  { m: 30, label: "30 min before", icon: "⌛" },
  { m: 60, label: "1 hr before", icon: "🕐" },
  { m: 1440, label: "1 day before", icon: "📅" },
];

// Dummy data
const DUMMY_RULES = [
  {
    id: "1",
    name: "Meeting Reminder",
    appliesTo: "all",
    channels: ["teams", "whatsapp"],
    timingMinutes: 15,
    meetingTypeFilter: "all",
    department: "",
    status: "enabled",
    priority: 1,
    hasConflict: false,
  },
  {
    id: "2",
    name: "External Client Reminder",
    appliesTo: "external",
    channels: ["email", "whatsapp"],
    timingMinutes: 60,
    meetingTypeFilter: "client",
    department: "",
    status: "enabled",
    priority: 2,
    hasConflict: false,
  },
  {
    id: "3",
    name: "Internal Team Reminder",
    appliesTo: "internal",
    channels: ["teams"],
    timingMinutes: 30,
    meetingTypeFilter: "internal",
    department: "",
    status: "enabled",
    priority: 3,
    hasConflict: true,
    conflictHint: "This rule may conflict with another rule",
  },
  {
    id: "4",
    name: "Sales Department Reminder",
    appliesTo: "department",
    channels: ["teams", "email"],
    timingMinutes: 1440,
    meetingTypeFilter: "all",
    department: "Sales",
    status: "disabled",
    priority: 4,
    hasConflict: false,
  },
  {
    id: "5",
    name: "Video Call Reminder",
    appliesTo: "all",
    channels: ["whatsapp"],
    timingMinutes: 5,
    meetingTypeFilter: "video_only",
    department: "",
    status: "enabled",
    priority: 5,
    hasConflict: false,
  },
  {
    id: "6",
    name: "Follow-up Reminder",
    appliesTo: "all",
    channels: ["email"],
    timingMinutes: 1440,
    meetingTypeFilter: "all",
    department: "",
    status: "disabled",
    priority: 6,
    hasConflict: false,
  },
];

const getChannelIcon = (channel) => {
  switch (channel.toLowerCase()) {
    case "teams":
      return <Video size={14} />;
    case "whatsapp":
      return <MessageSquare size={14} />;
    case "email":
      return <Mail size={14} />;
    default:
      return <Bell size={14} />;
  }
};

const getTimingInfo = (minutes) => {
  return TIMING.find((t) => t.m === minutes) || TIMING[0];
};

export default function ReminderMatrix() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [drawer, setDrawer] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({
    name: "",
    appliesTo: "all",
    channels: ["teams"],
    timingMinutes: 15,
    meetingTypeFilter: "all",
    department: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 500));
    setRows(DUMMY_RULES);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreateDrawer = () => {
    setIsEditing(false);
    setForm({
      name: "",
      appliesTo: "all",
      channels: ["teams"],
      timingMinutes: 15,
      meetingTypeFilter: "all",
      department: "",
    });
    setDrawer({ id: null, isNew: true });
  };

  const openEditDrawer = (rule) => {
    setIsEditing(true);
    setForm({
      name: rule.name,
      appliesTo: rule.appliesTo,
      channels: rule.channels || [],
      timingMinutes: rule.timingMinutes,
      meetingTypeFilter: rule.meetingTypeFilter,
      department: rule.department || "",
    });
    setDrawer(rule);
  };

  const move = (id, dir) => {
    const idx = rows.findIndex((r) => r.id === id);
    const next = [...rows];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    // Update priorities
    const updated = next.map((rule, index) => ({
      ...rule,
      priority: index + 1,
    }));
    setRows(updated);
    toast.success("Priority order updated");
  };

  const handleStatusToggle = (id, currentStatus) => {
    const newStatus = currentStatus === "enabled" ? "disabled" : "enabled";
    setRows((prev) =>
      prev.map((rule) =>
        rule.id === id ? { ...rule, status: newStatus } : rule,
      ),
    );
    toast.success(`Rule ${newStatus === "enabled" ? "enabled" : "disabled"}`);
  };

  const handleCreateRule = () => {
    // Validate form
    if (!form.name.trim()) {
      toast.error("Please enter a rule name");
      return;
    }
    if (form.channels.length === 0) {
      toast.error("Please select at least one channel");
      return;
    }

    const newRule = {
      id: String(Date.now()),
      name: form.name,
      appliesTo: form.appliesTo,
      channels: form.channels,
      timingMinutes: form.timingMinutes,
      meetingTypeFilter: form.meetingTypeFilter,
      department: form.appliesTo === "department" ? form.department : "",
      status: "enabled",
      priority: rows.length + 1,
      hasConflict: false,
    };
    setRows((prev) => [...prev, newRule]);
    toast.success("New rule created successfully");
    setDrawer(null);
  };

  const handleUpdateRule = (id, updatedData) => {
    // Validate form
    if (!updatedData.name.trim()) {
      toast.error("Please enter a rule name");
      return;
    }
    if (updatedData.channels.length === 0) {
      toast.error("Please select at least one channel");
      return;
    }

    setRows((prev) =>
      prev.map((rule) => (rule.id === id ? { ...rule, ...updatedData } : rule)),
    );
    toast.success("Rule updated successfully");
    setDrawer(null);
  };

  const stats = useMemo(() => {
    const total = rows.length;
    const enabled = rows.filter((r) => r.status === "enabled").length;
    const disabled = total - enabled;
    const channels = rows.reduce((acc, r) => {
      (r.channels || []).forEach((ch) => {
        acc[ch] = (acc[ch] || 0) + 1;
      });
      return acc;
    }, {});
    return { total, enabled, disabled, channels };
  }, [rows]);

  if (loading && !rows.length) {
    return (
      <div>
        <PageHeader
          title="Reminder matrix"
          breadcrumbs={[
            { label: "Company", path: companyUrl("dashboard") },
            { label: "Notifications" },
          ]}
        />
        <LoadingSkeleton rows={5} cols={4} />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <PageHeader
        title="Notification Reminders"
        breadcrumbs={[
          { label: "Company", path: companyUrl("dashboard") },
          { label: "Notifications" },
        ]}
        primaryAction={{
          label: "Add Rule",
          onClick: openCreateDrawer,
          className: styles.btnPrimary,
        }}
      />

      {/* Stats Cards */}
      <Row className="g-3 mb-4">
        <Col md={3}>
          <Card className={styles.statCard}>
            <Card.Body>
              <div className={styles.statIcon}>
                <Bell />
              </div>
              <div className={styles.statInfo}>
                <span className={styles.statLabel}>Total Rules</span>
                <span className={styles.statValue}>{stats.total}</span>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className={styles.statCard}>
            <Card.Body>
              <div className={styles.statIcon}>
                <CheckCircle />
              </div>
              <div className={styles.statInfo}>
                <span className={styles.statLabel}>Active</span>
                <span className={styles.statValue}>{stats.enabled}</span>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className={styles.statCard}>
            <Card.Body>
              <div className={styles.statIcon}>
                <XCircle />
              </div>
              <div className={styles.statInfo}>
                <span className={styles.statLabel}>Inactive</span>
                <span className={styles.statValue}>{stats.disabled}</span>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className={styles.statCard}>
            <Card.Body>
              <div className={styles.statIcon}>
                <MessageSquare />
              </div>
              <div className={styles.statInfo}>
                <span className={styles.statLabel}>Active Channels</span>
                <span className={styles.statValue}>
                  {Object.keys(stats.channels).length}
                </span>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Rules Grid */}
      <div className={styles.rulesHeader}>
        <h5 className={styles.rulesTitle}>Reminder Rules</h5>
        <p className={styles.rulesSubtitle}>
          Manage when and how reminders are sent to attendees
        </p>
      </div>

      <Row className="g-4">
        {rows.map((rule, index) => (
          <Col lg={6} xl={4} key={rule.id}>
            <Card
              className={`${styles.ruleCard} ${rule.status === "disabled" ? styles.ruleDisabled : ""}`}
            >
              <Card.Body>
                {/* Priority Badge */}
                <div className={styles.priorityBadge}>
                  <span>Priority {rule.priority}</span>
                  <div className={styles.priorityActions}>
                    <button
                      className={styles.moveBtn}
                      onClick={() => move(rule.id, -1)}
                      disabled={index === 0}
                    >
                      <ArrowUp size={14} />
                    </button>
                    <button
                      className={styles.moveBtn}
                      onClick={() => move(rule.id, 1)}
                      disabled={index === rows.length - 1}
                    >
                      <ArrowDown size={14} />
                    </button>
                  </div>
                </div>

                {/* Rule Header */}
                <div className={styles.ruleHeader}>
                  <div className={styles.ruleName}>
                    <h3>{rule.name}</h3>
                    {rule.hasConflict && (
                      <OverlayTrigger
                        overlay={<Tooltip>{rule.conflictHint}</Tooltip>}
                      >
                        <Flag size={16} className={styles.warningIcon} />
                      </OverlayTrigger>
                    )}
                  </div>
                  <Form.Check
                    type="switch"
                    checked={rule.status === "enabled"}
                    onChange={() => handleStatusToggle(rule.id, rule.status)}
                    className={styles.statusSwitch}
                  />
                </div>

                {/* Applies To */}
                <div className={styles.ruleSection}>
                  <div className={styles.sectionLabel}>
                    <Users size={14} />
                    <span>Applies To</span>
                  </div>
                  <div className={styles.sectionValue}>
                    {rule.appliesTo === "all" && "All meetings"}
                    {rule.appliesTo === "internal" && "Internal meetings only"}
                    {rule.appliesTo === "external" && "External meetings only"}
                    {rule.appliesTo === "department" &&
                      `Department: ${rule.department}`}
                  </div>
                </div>

                {/* Channels */}
                <div className={styles.ruleSection}>
                  <div className={styles.sectionLabel}>
                    <MessageSquare size={14} />
                    <span>Channels</span>
                  </div>
                  <div className={styles.channelsList}>
                    {(rule.channels || []).map((channel) => (
                      <Badge key={channel} className={styles.channelBadge}>
                        {getChannelIcon(channel)}
                        <span>{channel}</span>
                      </Badge>
                    ))}
                    {(!rule.channels || rule.channels.length === 0) && (
                      <span className={styles.emptyValue}>
                        No channels selected
                      </span>
                    )}
                  </div>
                </div>

                {/* Timing & Meeting Type */}
                <Row className="g-2">
                  <Col xs={6}>
                    <div className={styles.ruleSection}>
                      <div className={styles.sectionLabel}>
                        <Clock size={14} />
                        <span>Timing</span>
                      </div>
                      <div className={styles.timingValue}>
                        <span className={styles.timingIcon}>
                          {getTimingInfo(rule.timingMinutes).icon}
                        </span>
                        <span>{getTimingInfo(rule.timingMinutes).label}</span>
                      </div>
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div className={styles.ruleSection}>
                      <div className={styles.sectionLabel}>
                        <Calendar size={14} />
                        <span>Meeting Type</span>
                      </div>
                      <div className={styles.sectionValue}>
                        {rule.meetingTypeFilter === "all" && "All types"}
                        {rule.meetingTypeFilter === "client" &&
                          "Client meetings"}
                        {rule.meetingTypeFilter === "internal" &&
                          "Internal meetings"}
                        {rule.meetingTypeFilter === "video_only" &&
                          "Video calls only"}
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* Actions */}
                <div className={styles.ruleActions}>
                  <button
                    className={styles.editBtn}
                    onClick={() => openEditDrawer(rule)}
                  >
                    <Edit2 size={14} />
                    Edit Rule
                  </button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {rows.length === 0 && !loading && (
        <div className={styles.emptyState}>
          <Bell size={48} />
          <h4>No reminder rules configured</h4>
          <p>Create your first reminder rule to start sending notifications</p>
          <Button className={styles.btnPrimary} onClick={openCreateDrawer}>
            <Plus size={16} />
            Add Your First Rule
          </Button>
        </div>
      )}

      {/* Preview Timeline */}
      <Card className={styles.previewCard}>
        <Card.Body>
          <div className={styles.previewHeader}>
            <Bell size={18} />
            <span>Preview Timeline — Example: Meeting at 10:00 AM Today</span>
          </div>
          <div className={styles.timeline}>
            <div className={styles.timelineItem}>
              <div className={styles.timelineTime}>Day -1 (09:00)</div>
              <div className={styles.timelineContent}>
                <Badge className={styles.timelineBadge}>WhatsApp</Badge>
                <span>1 day before reminder</span>
              </div>
            </div>
            <div className={styles.timelineLine} />
            <div className={styles.timelineItem}>
              <div className={styles.timelineTime}>Same day (09:00)</div>
              <div className={styles.timelineContent}>
                <Badge className={styles.timelineBadge}>Teams</Badge>
                <span>Morning reminder</span>
              </div>
            </div>
            <div className={styles.timelineLine} />
            <div className={styles.timelineItem}>
              <div className={styles.timelineTime}>T-15 min (09:45)</div>
              <div className={styles.timelineContent}>
                <Badge className={styles.timelineBadge}>Email</Badge>
                <span>Final reminder</span>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Create/Edit Drawer */}
      <DrawerPanel
        isOpen={Boolean(drawer)}
        onClose={() => setDrawer(null)}
        title={drawer?.isNew ? "Create New Rule" : drawer?.name || "Edit Rule"}
      >
        <div className={styles.drawerContent}>
          <Form.Group className={styles.formGroup}>
            <Form.Label>Rule Name *</Form.Label>
            <Form.Control
              value={form.name || ""}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={styles.formControl}
              placeholder="e.g., Meeting Reminder - 15 min"
              autoFocus
            />
          </Form.Group>

          <Form.Label className={styles.formLabel}>Applies To *</Form.Label>
          <div className={styles.radioGroup}>
            {["all", "internal", "external", "department"].map((v) => (
              <Form.Check
                key={v}
                type="radio"
                name="applies"
                label={
                  v === "all"
                    ? "All meetings"
                    : v === "internal"
                      ? "Internal meetings only"
                      : v === "external"
                        ? "External meetings only"
                        : "Specific department"
                }
                checked={form.appliesTo === v}
                onChange={() =>
                  setForm({
                    ...form,
                    appliesTo: v,
                    department: v === "department" ? form.department : "",
                  })
                }
                className={styles.radioItem}
              />
            ))}
          </div>

          {form.appliesTo === "department" && (
            <Form.Control
              className={styles.formControl}
              style={{ marginBottom: "1rem" }}
              placeholder="Department name"
              value={form.department}
              onChange={(e) => setForm({ ...form, department: e.target.value })}
            />
          )}

          <Form.Label className={styles.formLabel}>Channels *</Form.Label>
          <div className={styles.checkboxGroup}>
            {CHANNEL_OPTS.map((c) => (
              <Form.Check
                key={c}
                type="checkbox"
                label={c}
                checked={(form.channels || []).includes(c.toLowerCase())}
                onChange={(e) => {
                  const ch = (form.channels || []).filter(
                    (x) => x !== c.toLowerCase(),
                  );
                  if (e.target.checked) ch.push(c.toLowerCase());
                  setForm({ ...form, channels: ch });
                }}
                className={styles.checkboxItem}
              />
            ))}
          </div>

          <Form.Group className={styles.formGroup}>
            <Form.Label>Timing *</Form.Label>
            <Form.Select
              value={form.timingMinutes}
              onChange={(e) =>
                setForm({ ...form, timingMinutes: Number(e.target.value) })
              }
              className={styles.formControl}
            >
              {TIMING.map((t) => (
                <option key={t.m} value={t.m}>
                  {t.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className={styles.formGroup}>
            <Form.Label>Meeting Type Filter</Form.Label>
            <Form.Select
              value={form.meetingTypeFilter}
              onChange={(e) =>
                setForm({ ...form, meetingTypeFilter: e.target.value })
              }
              className={styles.formControl}
            >
              <option value="all">All meetings</option>
              <option value="client">Client meetings only</option>
              <option value="internal">Internal meetings only</option>
              <option value="video_only">Video calls only</option>
            </Form.Select>
          </Form.Group>

          <div className={styles.drawerActions}>
            <Button
              className={styles.btnPrimary}
              onClick={() => {
                if (drawer?.isNew) {
                  handleCreateRule();
                } else {
                  handleUpdateRule(drawer.id, form);
                }
              }}
            >
              {drawer?.isNew ? "Create Rule" : "Save Changes"}
            </Button>
            <Button
              variant="light"
              onClick={() => setDrawer(null)}
              className={styles.btnSecondary}
            >
              Cancel
            </Button>
          </div>
        </div>
      </DrawerPanel>
    </div>
  );
}
